import { useState } from "react";
import { Link } from "react-router-dom";
import { SearchBar } from "../components/SearchBar";
import { Layout, Wordmark } from "../components/Layout";
import { useHead } from "../lib/useHead";
import { getHistory, clearHistory } from "../lib/history";
import { StatusBadge } from "../components/StatusBadge";
import { Hash, Type, FileText, ShieldCheck, Clock, X, Building2 } from "lucide-react";

const SearchTypeCard = ({ icon: Icon, title, example, testId }) => (
  <div data-testid={testId} className="rounded-lg border border-border bg-[hsl(var(--surface))] p-5">
    <span className="flex h-8 w-8 items-center justify-center rounded-md bg-[hsl(var(--brand-muted))] text-[hsl(var(--brand))]">
      <Icon className="h-4 w-4" />
    </span>
    <h3 className="mt-3 font-display text-sm font-semibold text-[hsl(var(--fg-primary))]">{title}</h3>
    <p className="mt-1 font-mono text-xs text-[hsl(var(--fg-muted))]">{example}</p>
  </div>
);

export default function Home() {
  useHead({
    title: "InfoSociété — Recherche d'entreprises françaises",
    description: "Retrouvez les informations publiques d'une entreprise française : recherche par nom, SIREN, SIRET ou mention RCS. Service privé indépendant.",
    robots: "index,follow",
  });
  const [history, setHistory] = useState(() => getHistory());
  const resetHistory = () => { clearHistory(); setHistory([]); };
  return (
    <Layout>
      <section className="relative overflow-hidden">
        <div className="grain pointer-events-none absolute inset-0 opacity-50" />
        <div className="relative mx-auto max-w-3xl px-5 pb-16 pt-14 sm:pt-20">
          <div className="flex justify-center">
            <Wordmark
              testId="home-logo"
              className="h-14 w-auto max-w-full object-contain sm:h-20"
            />
          </div>
          <div className="mt-6 flex justify-center">
            <span className="inline-flex items-center gap-2 rounded-full border border-border bg-[hsl(var(--surface))] px-3 py-1 text-xs font-medium text-[hsl(var(--fg-secondary))]">
              <ShieldCheck className="h-3.5 w-3.5 text-[hsl(var(--brand))]" />
              Données publiques officielles · INSEE / RNE
            </span>
          </div>

          <h1 className="mt-6 text-center font-display text-4xl font-semibold leading-none tracking-tight text-[hsl(var(--fg-primary))] sm:text-5xl lg:text-6xl">
            Retrouvez les informations<br className="hidden sm:block" /> publiques d'une entreprise française.
          </h1>
          <p className="mx-auto mt-5 max-w-xl text-center text-base leading-relaxed text-[hsl(var(--fg-secondary))]">
            Recherchez une société par son nom, son SIREN, son SIRET ou une mention
            RCS. Consultez sa fiche, vérifiez ses coordonnées et copiez-les en un clic.
          </p>

          <div className="mx-auto mt-9 max-w-2xl">
            <SearchBar size="lg" autoFocus />
            <p className="mt-3 text-center text-xs text-[hsl(var(--fg-muted))]">
              Exemples : « Danone », « 552 032 534 », « RCS Paris 552 032 534 »
            </p>
          </div>
        </div>
      </section>

      {history.length > 0 && (
        <section data-testid="history-section" className="mx-auto mb-10 max-w-3xl px-5">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[hsl(var(--fg-muted))]">
              <Clock className="h-3.5 w-3.5" /> Entreprises consultées récemment
            </h2>
            <button data-testid="history-clear" onClick={resetHistory} className="inline-flex items-center gap-1 text-xs text-[hsl(var(--brand))] hover:underline">
              <X className="h-3 w-3" /> Effacer
            </button>
          </div>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {history.map((h) => (
              <Link
                key={h.siren}
                to={`/entreprise/${h.siren}`}
                data-testid={`history-item-${h.siren}`}
                className="group flex items-center gap-3 rounded-lg border border-border bg-[hsl(var(--surface))] px-4 py-3 transition-colors hover:bg-[hsl(var(--brand-muted))]/30"
              >
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-border bg-[hsl(var(--background))] text-[hsl(var(--fg-muted))]">
                  <Building2 className="h-4 w-4" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="flex items-center gap-2">
                    <span className="truncate font-medium text-[hsl(var(--fg-primary))]">{h.nom}</span>
                    {h.etat && <StatusBadge etat={h.etat} />}
                  </span>
                  <span className="mt-0.5 block font-mono text-xs text-[hsl(var(--fg-muted))]">
                    SIREN {h.siren.replace(/(\d{3})(\d{3})(\d{3})/, "$1 $2 $3")}{h.ville ? ` · ${h.ville}` : ""}
                  </span>
                </span>
              </Link>
            ))}
          </div>
        </section>
      )}

      <section className="mx-auto max-w-3xl px-5">
        <h2 className="mb-4 text-xs uppercase tracking-[0.2em] text-[hsl(var(--fg-muted))]">
          Trois façons de rechercher
        </h2>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <SearchTypeCard
            testId="card-numero"
            icon={Hash}
            title="Par SIREN ou SIRET"
            example="9 ou 14 chiffres, avec ou sans espaces"
          />
          <SearchTypeCard
            testId="card-nom"
            icon={Type}
            title="Par nom"
            example="Raison sociale ou nom commercial"
          />
          <SearchTypeCard
            testId="card-rcs"
            icon={FileText}
            title="Par mention RCS"
            example="« RCS Paris 123 456 789 »"
          />
        </div>
      </section>

      <section className="mx-auto mt-14 max-w-3xl px-5">
        <div className="rounded-lg border border-border bg-[hsl(var(--surface))] p-6">
          <h2 className="font-display text-lg font-semibold text-[hsl(var(--fg-primary))]">
            Une consultation simple et transparente
          </h2>
          <p className="mt-2 text-sm leading-relaxed text-[hsl(var(--fg-secondary))]">
            InfoSociété affiche uniquement des informations issues de sources
            publiques officielles, avec la date de récupération. Aucune donnée n'est
            inventée : une information absente est clairement signalée comme « Non
            disponible ». InfoSociété est un service privé indépendant, sans lien avec
            une administration publique.
          </p>
        </div>
      </section>
    </Layout>
  );
}
